import { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { AA_SUPPORT_TYPES, AA_CATEGORIES, AA_DISCLAIMER } from "@/lib/askAnything";
import { Paperclip, X, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

const MAX = 5000;

export function AskQuestionDialog({ open, onClose, onCreated }) {
  const [message, setMessage] = useState("");
  const [supportType, setSupportType] = useState("");
  const [category, setCategory] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const reset = () => { setMessage(""); setSupportType(""); setCategory(""); setAttachment(null); setAccepted(false); };

  const pickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/ask-anything/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAttachment(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed."); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const submit = async () => {
    if (!message.trim() || !supportType || !category || !accepted) return;
    setSubmitting(true);
    try {
      const { data: res } = await api.post("/ask-anything/threads", {
        message: message.trim(), support_type: supportType, category,
        attachments: attachment ? [attachment] : [], disclaimer_accepted: true,
      });
      if (res?.safety_notice) toast.warning(res.safety_notice, { duration: 14000 });
      else toast.success("We've got your message. We'll reply within 72 hours.");
      reset(); onCreated?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not send. Please try again."); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="ask-question-dialog">
        <DialogHeader>
          <DialogTitle>What's on your mind?</DialogTitle>
          <DialogDescription>It doesn't have to be perfectly written. A few sentences is enough.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Textarea value={message} onChange={(e) => setMessage(e.target.value.slice(0, MAX))} rows={5} data-testid="aa-message"
              placeholder="You can ask a question, share a worry, tell us about a tough day, celebrate a small win, or explain where you feel stuck."
              className="rounded-xl resize-none" />
            <div className="text-xs text-kid-muted text-right">{message.length}/{MAX}</div>
          </div>
          <div className="space-y-1.5">
            <Label>What kind of support would feel most helpful?</Label>
            <select value={supportType} onChange={(e) => setSupportType(e.target.value)} data-testid="aa-support-type" className="h-11 w-full rounded-xl border border-kid-line px-3 bg-white text-sm">
              <option value="">Choose…</option>
              {AA_SUPPORT_TYPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label>What does this relate to?</Label>
            <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="aa-category" className="h-11 w-full rounded-xl border border-kid-line px-3 bg-white text-sm">
              <option value="">Choose…</option>
              {AA_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label>Attach a file <span className="text-kid-muted font-normal">(optional)</span></Label>
            {attachment ? (
              <div className="flex items-center justify-between bg-kid-mist rounded-xl px-3 py-2 text-sm" data-testid="aa-attachment">
                <span className="truncate text-kid-navy">{attachment.file_name}</span>
                <button onClick={() => setAttachment(null)} className="text-kid-muted hover:text-kid-coral" data-testid="aa-attachment-remove"><X size={16} /></button>
              </div>
            ) : (
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                className="inline-flex items-center gap-2 text-sm text-kid-teal font-poppins font-semibold border border-dashed border-kid-line rounded-xl px-4 h-11 hover:border-kid-teal transition-colors disabled:opacity-60" data-testid="aa-attach-btn">
                {uploading ? <Loader2 size={16} className="animate-spin" /> : <Paperclip size={16} />} {uploading ? "Uploading…" : "Add a file"}
              </button>
            )}
            <input ref={fileRef} type="file" className="hidden" onChange={pickFile} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" data-testid="aa-file-input" />
          </div>
          <label className="flex items-start gap-3 bg-kid-mist rounded-xl p-3 cursor-pointer" data-testid="aa-disclaimer">
            <Checkbox checked={accepted} onCheckedChange={setAccepted} data-testid="aa-disclaimer-check" className="mt-0.5" />
            <span className="text-xs text-kid-muted leading-relaxed">{AA_DISCLAIMER}</span>
          </label>
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="kid-btn kid-btn-ghost h-11 px-5" data-testid="aa-cancel">Cancel</button>
            <button onClick={submit} disabled={submitting || uploading || !message.trim() || !supportType || !category || !accepted}
              className="kid-btn kid-btn-primary h-11 px-5 disabled:opacity-50" data-testid="aa-submit">
              <Send size={16} /> {submitting ? "Sending…" : "Send to KidPath"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
